// The Workout view's parser. Three markdown documents in, typed structures out.
//
// The documents are the source of truth and this file keeps no copy of them.
// It only survives while they keep the shape written at the top of each one,
// so the shape is spelled out here as well, once, and the two must agree:
//
//   programme.md
//     ## <n> · <name> — <cap>                   a tier
//       *<one line saying when it is for>*
//       **Variant <n>** — Core <letter>, then:   then a table
//       | Movement | Dose | Cue |
//     # The six ladders                         everything below, until a
//     ## <name> — <where used>                  `##` that is not a ladder
//       | L | Movement | Next rung when | Cue |
//       the current rung is `| **3** |` — `**` around the level, nothing else
//
//   core.md
//     # Core <letter> — <name>
//       | # | Movement | Time | Cue |
//       **<n> · <label>.** text                  one per row, same numbers
//
//   exercises.md
//     ## <n> · <name>                            optionally an *italic* line
//       **Target** — · **Where it is** — · **Right feels like** —
//       **Wrong feels or looks like** — (release entries: **Wrong feels like**)
//       **Avoid** — · then optionally **Sworkit** —
//     an entry with no labelled blocks that says "See entry <n>" is a
//     reference to that entry; anything under `# Parked` keeps its own labels
//
//   `Cue` is the number of an entry in exercises.md, several separated by
//   commas, or `—` when nothing has been written for that movement.
//
// Any departure from that is a failure, returned as such, and the view then
// shows the document as plain markdown and nothing else. It never renders a
// half-parsed movement and never fills in a field the document does not have.
// Fixing a failure means editing the document, not this file.
//
// There is nothing in these structures about when a rung was reached, how
// long it has been there or how many times it has moved, because the
// documents do not say and the view is not allowed to know.

export type Result<T> = { ok: true; value: T } | { ok: false; reason: string };

// ── programme.md ──────────────────────────────────────────────────────────

export interface MovementRow {
  movement: string;
  dose: string;
  cues: number[];
  /** Set when the row reads `<name>, current rung` and a ladder of that name exists. */
  ladder: string | null;
}

export interface Variant { n: number; core: string; rows: MovementRow[] }

export interface Tier {
  n: number;
  name: string;
  cap: string;
  /** The italic line under the heading: when this tier is for. */
  when: string | null;
  variants: Variant[];
  /** Free prose inside the tier section, as markdown, shown after the tables. */
  notes: string;
}

export interface Rung { level: number; movement: string; gate: string; cues: number[] }

export interface Ladder {
  name: string;
  where: string;
  rungs: Rung[];
  /** The level marked `**n**`, or null when no rung is marked. A cursor, nothing more. */
  current: number | null;
  /** Prose after the table, as markdown. */
  note: string;
}

export interface Programme {
  /** Everything before `# The four tiers`, as markdown. */
  preamble: string;
  /** Between `# The four tiers` and the first tier. */
  tiersIntro: string;
  tiers: Tier[];
  /** Between `# The six ladders` and the first ladder. */
  laddersIntro: string;
  ladders: Ladder[];
  /** Everything after the last ladder — equipment, still open, the superseded plan. */
  after: string;
}

// ── core.md ───────────────────────────────────────────────────────────────

export interface CoreStep {
  n: number;
  movement: string;
  time: string;
  cues: number[];
  label: string;
  text: string;
}

export interface Core {
  letter: string;
  name: string;
  /** Prose between the heading and the table. */
  intro: string;
  steps: CoreStep[];
  /** Prose after the last step paragraph, up to the next `#` heading. */
  trailing: string;
}

export interface Cores {
  /** Everything before the first `# Core`, as markdown — including the massage gun rules. */
  preamble: string;
  cores: Core[];
}

// ── exercises.md ──────────────────────────────────────────────────────────

export interface Block { label: string; text: string }

export interface Movement {
  n: number;
  name: string;
  /** The `# ...` section the entry sits under. */
  section: string;
  /** The italic line under the heading, if any. */
  note: string | null;
  kind: "cued" | "reference" | "parked";
  blocks: Block[];
  /** Reference entries only: the entry number pointed at. */
  ref: number | null;
  /** Reference entries only: the paragraph(s), as markdown. */
  prose: string;
  /** The `**Sworkit**` block, where the app's demo is the wrong movement. */
  sworkit: string | null;
}

export interface Exercises {
  /** Everything before the first `#` section, as markdown. */
  preamble: string;
  movements: Movement[];
}

// ── helpers ───────────────────────────────────────────────────────────────

const fail = (reason: string): Result<never> => ({ ok: false, reason });

const normalise = (text: string) => text.replace(/\r\n/g, "\n").split("\n");

const cells = (row: string) =>
  row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

const isRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
const isDivider = (line: string) => /^\s*\|(\s*:?-+:?\s*\|)+\s*$/.test(line);

/** `12`, `3, 13`, or `—`. Anything else is a document error. */
function parseCues(cell: string, where: string): Result<number[]> {
  const c = cell.trim();
  if (c === "—" || c === "-" || c === "") return { ok: true, value: [] };
  const parts = c.split(",").map((p) => p.trim());
  const out: number[] = [];
  for (const p of parts) {
    if (!/^\d+$/.test(p)) return fail(`${where}: cue "${cell}" is not a number or —`);
    out.push(Number(p));
  }
  return { ok: true, value: out };
}

/** Reads one table starting at `i`. Returns its rows and the index after it. */
function readTable(lines: string[], i: number, where: string, width: number): Result<{ header: string[]; rows: string[][]; next: number }> {
  if (!isRow(lines[i]) || i + 1 >= lines.length || !isDivider(lines[i + 1])) {
    return fail(`${where}: expected a table`);
  }
  const header = cells(lines[i]);
  if (header.length !== width) {
    return fail(`${where}: table has ${header.length} columns, expected ${width} (${header.join(" | ")})`);
  }
  let j = i + 2;
  const rows: string[][] = [];
  while (j < lines.length && isRow(lines[j])) {
    const r = cells(lines[j]);
    if (r.length !== width) return fail(`${where}: row "${lines[j].trim()}" has ${r.length} cells, expected ${width}`);
    rows.push(r);
    j++;
  }
  if (!rows.length) return fail(`${where}: table has no rows`);
  return { ok: true, value: { header, rows, next: j } };
}

const trimBlank = (lines: string[]) => {
  let a = 0, b = lines.length;
  while (a < b && !lines[a].trim()) a++;
  while (b > a && !lines[b - 1].trim()) b--;
  return lines.slice(a, b);
};

/** Free prose, kept as markdown. Horizontal rules are dropped: they are section furniture, not content. */
const md = (lines: string[]) => trimBlank(lines.filter((l) => !/^\s*(---+)\s*$/.test(l))).join("\n");

/** Reads a paragraph starting at `i` (until a blank line). */
function readParagraph(lines: string[], i: number): { text: string; next: number } {
  const buf: string[] = [];
  let j = i;
  while (j < lines.length && lines[j].trim() && !/^#/.test(lines[j]) && !isRow(lines[j])) {
    buf.push(lines[j].trim());
    j++;
  }
  return { text: buf.join(" "), next: j };
}

const isItalicLine = (text: string) => /^\*[^*].*[^*]\*$/.test(text) || /^\*[^*]\*$/.test(text);

// ── programme ─────────────────────────────────────────────────────────────

const TIER_HEADING = /^## (\d+) · (.+?) — (.+)$/;
const VARIANT_LINE = /^\*\*Variant (\d+)\*\* — Core ([A-Za-z]), then:$/;
const LADDER_HEADING = /^## (.+?) — (.+)$/;

export function parseProgramme(text: string): Result<Programme> {
  const lines = normalise(text);
  const tiersAt = lines.findIndex((l) => l.trim() === "# The four tiers");
  const laddersAt = lines.findIndex((l) => l.trim() === "# The six ladders");
  if (tiersAt === -1) return fail("programme: no `# The four tiers` heading");
  if (laddersAt === -1) return fail("programme: no `# The six ladders` heading");
  if (laddersAt < tiersAt) return fail("programme: the ladders come before the tiers");

  const preamble = md(lines.slice(0, tiersAt));

  // Tiers: every `## n · name — cap` between the two H1s.
  const tiers: Tier[] = [];
  let tiersIntroEnd = laddersAt;
  let i = tiersAt + 1;
  while (i < laddersAt) {
    const h = TIER_HEADING.exec(lines[i]);
    if (!h) {
      if (/^#/.test(lines[i]) && tiers.length === 0 && !TIER_HEADING.test(lines[i])) {
        return fail(`programme: unexpected heading before the first tier: "${lines[i]}"`);
      }
      if (/^#/.test(lines[i])) return fail(`programme: unexpected heading inside the tiers: "${lines[i]}"`);
      i++;
      continue;
    }
    if (tiers.length === 0) tiersIntroEnd = i;
    const where = `programme, tier ${h[1]} (${h[2]})`;
    const tier: Tier = { n: Number(h[1]), name: h[2].trim(), cap: h[3].trim(), when: null, variants: [], notes: "" };
    const notes: string[] = [];
    i++;
    while (i < laddersAt && !/^#/.test(lines[i])) {
      const line = lines[i];
      if (!line.trim() || /^\s*---+\s*$/.test(line)) { i++; continue; }
      const v = VARIANT_LINE.exec(line.trim());
      if (v) {
        let j = i + 1;
        while (j < laddersAt && !lines[j].trim()) j++;
        const t = readTable(lines, j, `${where}, variant ${v[1]}`, 3);
        if (!t.ok) return t;
        if (t.value.header.join("|") !== "Movement|Dose|Cue") {
          return fail(`${where}, variant ${v[1]}: table header is "${t.value.header.join(" | ")}", expected Movement | Dose | Cue`);
        }
        const rows: MovementRow[] = [];
        for (const r of t.value.rows) {
          const cues = parseCues(r[2], `${where}, "${r[0]}"`);
          if (!cues.ok) return cues;
          rows.push({ movement: r[0], dose: r[1], cues: cues.value, ladder: null });
        }
        tier.variants.push({ n: Number(v[1]), core: v[2].toUpperCase(), rows });
        i = t.value.next;
        continue;
      }
      if (isRow(line)) return fail(`${where}: a table without a **Variant n** line before it`);
      const p = readParagraph(lines, i);
      if (tier.when === null && tier.variants.length === 0 && isItalicLine(p.text)) {
        tier.when = p.text.slice(1, -1);
      } else {
        notes.push(p.text);
      }
      i = p.next;
    }
    if (tier.variants.length !== 2) return fail(`${where}: has ${tier.variants.length} variants, expected two`);
    if (tier.variants[0].n !== 1 || tier.variants[1].n !== 2) return fail(`${where}: variants are not numbered 1 then 2`);
    tier.notes = notes.join("\n\n");
    tiers.push(tier);
  }
  if (!tiers.length) return fail("programme: no tiers found");
  const tiersIntro = md(lines.slice(tiersAt + 1, tiersIntroEnd));

  // Ladders: `## name — where` under the ladders H1, until a `##` that is not one, or any `#`.
  const ladders: Ladder[] = [];
  let laddersIntroEnd = lines.length;
  let afterAt = lines.length;
  i = laddersAt + 1;
  while (i < lines.length) {
    const line = lines[i];
    if (/^# /.test(line)) { afterAt = i; break; }
    const h = LADDER_HEADING.exec(line);
    if (/^#/.test(line) && !h) { afterAt = i; break; }
    if (!h) { i++; continue; }
    if (ladders.length === 0) laddersIntroEnd = i;
    const where = `programme, ladder "${h[1]}"`;
    let j = i + 1;
    while (j < lines.length && !lines[j].trim()) j++;
    const t = readTable(lines, j, where, 4);
    if (!t.ok) return t;
    if (t.value.header.join("|") !== "L|Movement|Next rung when|Cue") {
      return fail(`${where}: table header is "${t.value.header.join(" | ")}", expected L | Movement | Next rung when | Cue`);
    }
    const rungs: Rung[] = [];
    let current: number | null = null;
    for (const r of t.value.rows) {
      const marked = /^\*\*(\d+)\*\*$/.exec(r[0]);
      const plain = /^(\d+)$/.exec(r[0]);
      if (!marked && !plain) return fail(`${where}: level "${r[0]}" is not a number, or is marked with something other than **`);
      const level = Number((marked ?? plain)![1]);
      if (level !== rungs.length + 1) return fail(`${where}: levels are not 1, 2, 3… in order (found ${level} at position ${rungs.length + 1})`);
      if (marked) {
        if (current !== null) return fail(`${where}: more than one rung is marked as current`);
        current = level;
      }
      const cues = parseCues(r[3], `${where}, level ${level}`);
      if (!cues.ok) return cues;
      rungs.push({ level, movement: r[1], gate: r[2], cues: cues.value });
    }
    // The note: everything up to the next heading.
    let k = t.value.next;
    const noteStart = k;
    while (k < lines.length && !/^#/.test(lines[k])) k++;
    ladders.push({ name: h[1].trim(), where: h[2].trim(), rungs, current, note: md(lines.slice(noteStart, k)) });
    i = k;
  }
  if (!ladders.length) return fail("programme: no ladders found under `# The six ladders`");
  const laddersIntro = md(lines.slice(laddersAt + 1, laddersIntroEnd));
  const after = md(lines.slice(afterAt));

  // `<name>, current rung` points at the ladder of that name, when there is one.
  const byName = new Map(ladders.map((l) => [l.name.toLowerCase(), l.name]));
  for (const t of tiers) for (const v of t.variants) for (const r of v.rows) {
    const m = /^(.+?), current rung$/i.exec(r.movement);
    if (m) r.ladder = byName.get(m[1].toLowerCase()) ?? null;
  }

  return { ok: true, value: { preamble, tiersIntro, tiers, laddersIntro, ladders, after } };
}

// ── core ──────────────────────────────────────────────────────────────────

const CORE_HEADING = /^# Core ([A-Za-z]) — (.+)$/;
const STEP_PARA = /^\*\*(\d+) · (.+?)\.\*\*\s*(.*)$/;

export function parseCores(text: string): Result<Cores> {
  const lines = normalise(text);
  const starts: number[] = [];
  lines.forEach((l, idx) => { if (CORE_HEADING.test(l)) starts.push(idx); });
  if (!starts.length) return fail("core: no `# Core <letter> — <name>` heading");
  const preamble = md(lines.slice(0, starts[0]));

  const cores: Core[] = [];
  for (let s = 0; s < starts.length; s++) {
    const h = CORE_HEADING.exec(lines[starts[s]])!;
    const where = `core ${h[1]}`;
    // The section ends at the next H1, of any kind.
    let end = lines.length;
    for (let k = starts[s] + 1; k < lines.length; k++) if (/^# /.test(lines[k])) { end = k; break; }

    let i = starts[s] + 1;
    let tableAt = -1;
    for (let k = i; k < end; k++) if (isRow(lines[k]) && k + 1 < end && isDivider(lines[k + 1])) { tableAt = k; break; }
    if (tableAt === -1) return fail(`${where}: no table`);
    const intro = md(lines.slice(i, tableAt));
    const t = readTable(lines, tableAt, where, 4);
    if (!t.ok) return t;
    if (t.value.header.join("|") !== "#|Movement|Time|Cue") {
      return fail(`${where}: table header is "${t.value.header.join(" | ")}", expected # | Movement | Time | Cue`);
    }
    const rows = t.value.rows;
    for (let idx = 0; idx < rows.length; idx++) {
      if (!/^\d+$/.test(rows[idx][0]) || Number(rows[idx][0]) !== idx + 1) {
        return fail(`${where}: rows are not numbered 1, 2, 3… (found "${rows[idx][0]}" at position ${idx + 1})`);
      }
    }

    // The paragraphs, one per row, same numbers, same order.
    const steps: CoreStep[] = [];
    i = t.value.next;
    let lastStepEnd = i;
    while (i < end) {
      if (!lines[i].trim()) { i++; continue; }
      const m = STEP_PARA.exec(lines[i].trim());
      if (!m) {
        if (steps.length === 0) return fail(`${where}: expected a **1 · label.** paragraph after the table, found "${lines[i].trim().slice(0, 40)}"`);
        break;
      }
      const p = readParagraph(lines, i);
      const n = Number(m[1]);
      if (n !== steps.length + 1) return fail(`${where}: paragraph ${n} out of order (expected ${steps.length + 1})`);
      const row = rows[n - 1];
      if (!row) return fail(`${where}: paragraph ${n} has no table row`);
      const cues = parseCues(row[3], `${where}, step ${n}`);
      if (!cues.ok) return cues;
      // The text after the bold label, with the label's own words removed from the joined paragraph.
      const full = p.text;
      const textStart = full.indexOf(".**") + 3;
      steps.push({ n, movement: row[1], time: row[2], cues: cues.value, label: m[2], text: full.slice(textStart).trim() });
      i = p.next;
      lastStepEnd = i;
    }
    if (steps.length !== rows.length) return fail(`${where}: ${rows.length} rows but ${steps.length} paragraphs`);
    cores.push({ letter: h[1].toUpperCase(), name: h[2].trim(), intro, steps, trailing: md(lines.slice(lastStepEnd, end)) });
  }
  return { ok: true, value: { preamble, cores } };
}

// ── exercises ─────────────────────────────────────────────────────────────

const ENTRY_HEADING = /^## (\d+) · (.+)$/;
const BLOCK = /^\*\*([^*]+)\*\* — (.*)$/;
const CUED_LABELS = ["Target", "Where it is", "Right feels like", "Wrong feels or looks like", "Avoid"];
const WRONG_RELEASE = "Wrong feels like";
const SWORKIT = "Sworkit";

export function parseExercises(text: string): Result<Exercises> {
  const lines = normalise(text);
  const firstSection = lines.findIndex((l, idx) => idx > 0 && /^# /.test(l));
  if (firstSection === -1) return fail("exercises: no `# ` section heading");
  const preamble = md(lines.slice(0, firstSection));

  const movements: Movement[] = [];
  let section = "";
  let i = firstSection;
  while (i < lines.length) {
    const line = lines[i];
    const h1 = /^# (.+)$/.exec(line);
    if (h1) { section = h1[1].trim(); i++; continue; }
    const h = ENTRY_HEADING.exec(line);
    if (!h) {
      if (/^#/.test(line)) return fail(`exercises: unexpected heading "${line}"`);
      i++;
      continue;
    }
    const n = Number(h[1]);
    const where = `exercises, entry ${n}`;
    if (movements.some((m) => m.n === n)) return fail(`${where}: number used twice`);
    const mv: Movement = { n, name: h[2].trim(), section, note: null, kind: "cued", blocks: [], ref: null, prose: "", sworkit: null };
    const prose: string[] = [];
    i++;
    while (i < lines.length && !/^#/.test(lines[i])) {
      if (!lines[i].trim() || /^\s*---+\s*$/.test(lines[i])) { i++; continue; }
      if (isRow(lines[i])) return fail(`${where}: a table, which entries do not have`);
      const p = readParagraph(lines, i);
      const b = BLOCK.exec(p.text);
      if (b) {
        mv.blocks.push({ label: b[1].trim(), text: b[2].trim() });
      } else if (mv.note === null && mv.blocks.length === 0 && prose.length === 0 && isItalicLine(p.text)) {
        mv.note = p.text.slice(1, -1);
      } else {
        prose.push(p.text);
      }
      i = p.next;
    }

    const labels = mv.blocks.map((b) => b.label);
    if (section === "Parked") {
      mv.kind = "parked";
      if (!mv.blocks.length) return fail(`${where}: parked entry has no labelled blocks`);
    } else if (!mv.blocks.length) {
      const ref = /See entry (\d+)/.exec(prose.join(" "));
      if (!ref) return fail(`${where}: no labelled blocks and no "See entry n"`);
      mv.kind = "reference";
      mv.ref = Number(ref[1]);
    } else {
      if (prose.length) return fail(`${where}: prose outside the labelled blocks: "${prose[0].slice(0, 40)}"`);
      const expected = CUED_LABELS.map((l, idx) => (idx === 3 && labels[3] === WRONG_RELEASE ? WRONG_RELEASE : l));
      const core = labels.slice(0, 5);
      if (core.join("|") !== expected.join("|")) {
        return fail(`${where}: blocks are [${labels.join(", ")}], expected [${expected.join(", ")}]`);
      }
      const extra = labels.slice(5);
      if (extra.length > 1 || (extra.length === 1 && extra[0] !== SWORKIT)) {
        return fail(`${where}: unexpected block(s) after Avoid: [${extra.join(", ")}]`);
      }
    }
    const sw = mv.blocks.find((b) => b.label === SWORKIT);
    if (sw) mv.sworkit = sw.text;
    mv.prose = prose.join("\n\n");
    movements.push(mv);
  }
  if (!movements.length) return fail("exercises: no `## n · name` entries");

  // A reference must point at something that exists and is not itself a reference.
  for (const m of movements) {
    if (m.kind !== "reference") continue;
    const target = movements.find((x) => x.n === m.ref);
    if (!target) return fail(`exercises, entry ${m.n}: points at entry ${m.ref}, which does not exist`);
    if (target.kind === "reference") return fail(`exercises, entry ${m.n}: points at entry ${m.ref}, which is itself a reference`);
  }
  return { ok: true, value: { preamble, movements } };
}

// ── cross-checks ──────────────────────────────────────────────────────────

/**
 * Every cue in the programme and the cores must name an entry that exists.
 * A dangling number is a document error in the document that carries it, so
 * it is reported against that document and that document fails to parse.
 */
export function checkCues(programme: Programme | null, cores: Cores | null, exercises: Exercises): { programme: string | null; cores: string | null } {
  const have = new Set(exercises.movements.map((m) => m.n));
  const missing = (cues: number[]) => cues.filter((c) => !have.has(c));
  let p: string | null = null;
  let c: string | null = null;
  if (programme) {
    for (const t of programme.tiers) for (const v of t.variants) for (const r of v.rows) {
      const m = missing(r.cues);
      if (m.length && !p) p = `programme, tier ${t.n}, "${r.movement}": cue ${m.join(", ")} is not an entry in exercises.md`;
    }
    for (const l of programme.ladders) for (const r of l.rungs) {
      const m = missing(r.cues);
      if (m.length && !p) p = `programme, ladder "${l.name}", level ${r.level}: cue ${m.join(", ")} is not an entry in exercises.md`;
    }
  }
  if (cores) {
    for (const k of cores.cores) for (const s of k.steps) {
      const m = missing(s.cues);
      if (m.length && !c) c = `core ${k.letter}, step ${s.n}: cue ${m.join(", ")} is not an entry in exercises.md`;
    }
  }
  return { programme: p, cores: c };
}
